/* eslint-disable react/jsx-no-useless-fragment */
import { MenuCategoriesProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { GridComponent } from '../../../../../base/grid/Grid';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import {
  getLastItemIndexOfCategory,
  getScaleSize,
  isBetweenRange,
  processItemsWithModifiers,
} from '../../../../../utils/utils';
import { memo } from 'react';
import Item from '../Item';
import { TEMPLATE_2_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';

interface CategoryProps {
  template: Template;
  category: MenuCategoriesProps;
  categoryIndex?: number;
  development?: boolean;
  menuIndex: number;
  timezone?: string;
  scale?: number;
  fontFamily?: string;
}

const Category = ({
  category,
  categoryIndex,
  template,
  development,
  menuIndex,
  timezone,
  scale = 1,
  fontFamily: fontFamilyProp,
}: CategoryProps) => {
  const { normalizePixels } = useNormalizedPixels();

  const sortedItems = processItemsWithModifiers(category.items, TEMPLATE_2_MODIFIERS);
  const isDevelopment = development ? true : isBetweenRange(category.schedules, timezone);
  const lastItemIndex = getLastItemIndexOfCategory(sortedItems) ?? -1;
  const hasAnyModifier = sortedItems.some((item) => item.hasValidModifiers);
  const isCategoryEnabled = template?.category?.isEnabled ?? true;
  const isModifier = (hasAnyModifier || sortedItems.length === 0) && template?.itemPrice?.isEnabled;
  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const templateLayout1 = template.layoutType === 1;
  const fontFamily = fontFamilyProp || template?.fontFamilyName;

  return (
    <>
      {isDevelopment && (
        <>
          {(isCategoryEnabled || isModifier) && (
            <BoxComponent
              sx={{
                width: isMedia && templateLayout1 ? '33%' : '24%',
                height: 'auto',
                overflow: 'hidden',
              }}
            >
              {isCategoryEnabled && (
                <TypographyComponent
                  variant="h2"
                  sx={{
                    backgroundColor: template.category?.backgroundColor || '#3C3333',
                    fontWeight: template.category?.fontWeight ?? '400',
                    textTransform: 'uppercase',
                    fontFamily: fontFamily,
                    whiteSpace: 'nowrap',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '-webkit-box-orient': 'vertical',
                    '-webkit-line-clamp': '1',
                    color: template?.category?.color ? template?.category?.color : '#D7AD78',
                    fontSize: getScaleSize(
                      normalizePixels(22.5 * Number(template?.category?.textScale ?? 1), 'height'),
                      scale
                    ),
                    padding: `${
                      (getScaleSize(normalizePixels(12, 'height'), scale),
                      getScaleSize(normalizePixels(18, 'width'), scale))
                    }`,
                    fontStyle: template.category?.fontStyle,
                    letterSpacing: template.category?.letterSpacing,
                    lineHeight: template.category?.lineHeight,
                  }}
                >
                  {category.name}
                </TypographyComponent>
              )}

              {isModifier && (
                <BoxComponent
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginY: getScaleSize(normalizePixels(10, 'height'), scale),
                  }}
                >
                  <BoxComponent width={'40%'}>
                    <GridComponent container>
                      {['S', 'M'].map((val) => (
                        <GridComponent
                          key={val}
                          item
                          xs={6}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <TypographyComponent
                            sx={{
                              color: template.category?.underlineColor ? template.category?.underlineColor : '#000000',
                              fontWeight: '400',
                              textAlign: 'center',
                              fontFamily: fontFamily,
                              textTransform: 'uppercase',
                              borderRadius: '50%',
                              backgroundColor: '#D7AD78',
                              border: 'none',
                              fontSize: getScaleSize(normalizePixels(16, 'height'), scale),
                              lineHeight: getScaleSize(normalizePixels(20, 'height'), scale),
                              width: getScaleSize(normalizePixels(20, 'height'), scale),
                              height: getScaleSize(normalizePixels(20, 'height'), scale),
                            }}
                          >
                            {val}
                          </TypographyComponent>
                        </GridComponent>
                      ))}
                    </GridComponent>
                  </BoxComponent>
                </BoxComponent>
              )}
            </BoxComponent>
          )}

          {sortedItems?.map((item, itemIndex) => {
            if (item)
              return (
                <Item
                  key={item.id}
                  template={template}
                  item={item}
                  lastItemIndex={lastItemIndex}
                  index={itemIndex}
                  development={development}
                  timezone={timezone}
                  scale={scale}
                  hasAnyModifier={hasAnyModifier}
                  fontFamily={fontFamily}
                  categoryId={category?.id || 0}
                />
              );
          })}
        </>
      )}
    </>
  );
};

export default memo(Category);
